const STORAGE_PREFIX = 'evry-countdown';

/**
 * @param {string} key
 * @returns {string | null}
 */
function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 */
function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the bar still works for this page view.
  }
}

/**
 * "-0400" → -240 (minutes east of UTC).
 * @param {string | undefined} offset
 */
function parseOffset(offset) {
  const match = /^([+-])(\d{2})(\d{2})$/.exec(offset || '');
  if (!match) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

/**
 * "07:30" → 450 (minutes after midnight), or null.
 * @param {string | undefined} time
 */
function parseTime(time) {
  const match = /^(\d{1,2}):(\d{2})$/.exec((time || '').trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/** @param {number} value */
const pad = (value) => String(value).padStart(2, '0');

class CountdownBar extends HTMLElement {
  /** @type {number | undefined} */
  #interval;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    this.querySelector('[data-countdown-close]')?.addEventListener('click', this.#close);

    this.#resizeObserver = new ResizeObserver(this.#publishHeight);
    this.#resizeObserver.observe(this);
    this.#publishHeight();

    this.#tick();
    this.#interval = window.setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
    this.#resizeObserver?.disconnect();
    this.#setHidden(true);
  }

  get #isPreview() {
    return this.hasAttribute('data-preview');
  }

  get #closedKey() {
    return `${STORAGE_PREFIX}-closed:${this.dataset.sectionId}`;
  }

  #close = () => {
    writeStorage(this.#closedKey, this.dataset.campaign || '');
    if (!this.#isPreview) this.#stop(true);
  };

  /**
   * When the current countdown ends, in epoch ms: a number while it runs, 'wait' while it has
   * not started (or is outside today's window), 'ended' once it is over.
   * @param {number} now
   * @returns {number | 'wait' | 'ended'}
   */
  #target(now) {
    const { mode } = this.dataset;

    if (mode === 'evergreen') {
      const duration = Number(this.dataset.evergreenMinutes || 60) * 60_000;
      const key = `${STORAGE_PREFIX}-start:${this.dataset.sectionId}:${this.dataset.campaign}`;
      let start = Number(readStorage(key));
      if (!start || start > now) {
        start = now;
        writeStorage(key, String(start));
      }
      if (now >= start + duration) {
        if (this.dataset.evergreenRestart !== 'true') return 'ended';
        start += Math.floor((now - start) / duration) * duration;
        writeStorage(key, String(start));
      }
      return start + duration;
    }

    if (mode === 'daily') {
      const offset = parseOffset(this.dataset.storeOffset) * 60_000;
      const startMinutes = parseTime(this.dataset.dailyStart) ?? 0;
      const endMinutes = parseTime(this.dataset.dailyEnd) ?? 24 * 60 - 1;
      const weekdays = (this.dataset.weekdays || '').split(',').filter(Boolean).map(Number);

      const storeNow = new Date(now + offset);
      const minutesNow = storeNow.getUTCHours() * 60 + storeNow.getUTCMinutes();
      if (!weekdays.includes(storeNow.getUTCDay())) return 'wait';
      if (minutesNow < startMinutes || minutesNow >= endMinutes) return 'wait';

      const storeMidnight = Date.UTC(storeNow.getUTCFullYear(), storeNow.getUTCMonth(), storeNow.getUTCDate());
      return storeMidnight + endMinutes * 60_000 - offset;
    }

    const start = Number(this.dataset.start);
    const end = Number(this.dataset.end);
    if (!end) return 'ended';
    if (start && now < start) return 'wait';
    if (now >= end) return 'ended';
    return end;
  }

  #tick = () => {
    if (!this.#isPreview && readStorage(this.#closedKey) === this.dataset.campaign) {
      this.#stop(true);
      return;
    }

    const now = Date.now();
    const target = this.#target(now);

    if (target === 'wait') {
      this.#setHidden(!this.#isPreview);
      this.#render(0);
      return;
    }

    if (target === 'ended') {
      this.#render(0);
      if (this.dataset.onEnd === 'message' && this.querySelector('[ref="endedMessage"]')) {
        this.classList.add('countdown-bar--ended');
        this.#setHidden(false);
      } else {
        this.#setHidden(!this.#isPreview);
      }
      if (this.dataset.mode === 'fixed') window.clearInterval(this.#interval);
      return;
    }

    this.classList.remove('countdown-bar--ended');
    this.#setHidden(false);
    this.#render(Math.max(0, Math.floor((target - now) / 1000)));
  };

  /** @param {number} remaining seconds left */
  #render(remaining) {
    for (const timer of this.querySelectorAll('[data-countdown-timer]')) {
      if (!(timer instanceof HTMLElement)) continue;
      const showDays = timer.dataset.showDays || 'always';
      const days = Math.floor(remaining / 86400);
      const daysVisible = showDays === 'always' || (showDays === 'auto' && days > 0);
      const hours = daysVisible ? Math.floor((remaining % 86400) / 3600) : Math.floor(remaining / 3600);

      const values = {
        days: pad(days),
        hours: pad(hours),
        minutes: pad(Math.floor((remaining % 3600) / 60)),
        seconds: pad(remaining % 60),
      };

      for (const [unit, value] of Object.entries(values)) {
        const element = timer.querySelector(`[data-unit="${unit}"]`);
        if (element && element.textContent !== value) element.textContent = value;
      }

      for (const element of timer.querySelectorAll('.countdown-timer__unit--days, .countdown-timer__separator--days')) {
        if (element instanceof HTMLElement) element.hidden = !daysVisible;
      }
    }
  }

  /** @param {boolean} hidden */
  #setHidden(hidden) {
    if (this.hidden === hidden) return;
    this.hidden = hidden;
    this.#publishHeight();
  }

  /** @param {boolean} hide */
  #stop(hide) {
    window.clearInterval(this.#interval);
    if (hide) this.#setHidden(true);
  }

  #publishHeight = () => {
    if (!this.classList.contains('countdown-bar--sticky')) return;
    const height = this.hidden ? 0 : this.offsetHeight;
    document.body.style.setProperty('--countdown-bar-height', `${height}px`);
  };
}

if (!customElements.get('countdown-bar')) {
  customElements.define('countdown-bar', CountdownBar);
}
